import React, { useEffect, useState, useMemo } from 'react';
import { Box, Paper, Button, useTheme, Grid, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateStudentEnrollment } from '../../hooks/studentEnrollments/useCreateStudentEnrollment';
import { useUpdateStudentEnrollment } from '../../hooks/studentEnrollments/useUpdateStudentEnrollment';
import { useGetSchools } from '../../hooks/schools/useGetSchools';
import { useGetAcademicYears } from '../../hooks/academicYears/useGetAcademicYears';
import { useGetAcademicTerms } from '../../hooks/academicTerms/useGetAcademicTerms';
import { useGetLevels } from '../../hooks/levels/useGetLevels';
import { useGetClasses } from '../../hooks/classes/useGetClasses';
import { useGetStudentsForSelect } from '../../hooks/students/useGetStudentsForSelect';
import { useCurrentContext } from '../../hooks/useCurrentContext';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const STATUS_OPTS_AR = [
    { id: 'active', name: 'نشط' }, { id: 'transferred', name: 'منقول' },
    { id: 'completed', name: 'مكتمل' }, { id: 'cancelled', name: 'ملغي' },
];
const STATUS_OPTS_EN = [
    { id: 'active', name: 'Active' }, { id: 'transferred', name: 'Transferred' },
    { id: 'completed', name: 'Completed' }, { id: 'cancelled', name: 'Cancelled' },
];

const FormAddEditStudentEnrollment = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const isArabic = i18n.language === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createEnrollment, isPending: isCreating } = useCreateStudentEnrollment();
    const { editEnrollment, isUpdate } = useUpdateStudentEnrollment();
    const { isLoading: schoolsLoading, schools } = useGetSchools(1, 999);
    const { academicYears } = useGetAcademicYears(1, 999);
    const { academicTerms } = useGetAcademicTerms(1, 999);
    const { levels } = useGetLevels(1, 999);
    const { classes: classItems } = useGetClasses(1, 999);
    const { currentSchoolId, currentYearId, currentTermId } = useCurrentContext();

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        student_id: '', school_id: '', academic_year_id: '', academic_term_id: '',
        level_id: '', class_id: '', joined_at: '', left_at: '', status: 'active', notes: '',
    });

    const studentFilters = useMemo(() => ({
        school_id: form.school_id || undefined,
        exclude_enrolled_term_id: (!isEdit && form.academic_term_id) ? form.academic_term_id : undefined,
    }), [form.school_id, form.academic_term_id, isEdit]);

    const { studentsOptions } = useGetStudentsForSelect(studentFilters);

    // Auto-set current context for new records
    useEffect(() => {
        if (!isEdit) {
            setForm((p) => ({
                ...p,
                school_id: p.school_id || currentSchoolId || '',
                academic_year_id: p.academic_year_id || currentYearId || '',
                academic_term_id: p.academic_term_id || currentTermId || '',
            }));
        }
    }, [currentSchoolId, currentYearId, currentTermId, isEdit]);

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            student_id: currentData.student_id || '',
            school_id: currentData.school_id || '',
            academic_year_id: currentData.academic_year_id || '',
            academic_term_id: currentData.academic_term_id || '',
            level_id: currentData.level_id || '',
            class_id: currentData.class_id || '',
            joined_at: currentData.joined_at || '',
            left_at: currentData.left_at || '',
            status: currentData.status || 'active',
            notes: currentData.notes || '',
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            student_id: Number(form.student_id),
            school_id: Number(form.school_id),
            academic_year_id: Number(form.academic_year_id),
            academic_term_id: Number(form.academic_term_id),
            level_id: Number(form.level_id),
            class_id: Number(form.class_id),
            joined_at: form.joined_at || null,
            left_at: form.left_at || null,
            status: form.status,
            notes: form.notes || null,
        };
        if (isEdit && currentData?.id) { editEnrollment({ req: payload, id: currentData.id }); }
        else { createEnrollment(payload); }
    };

    if (schoolsLoading) return <LoadingPage />;

    const schoolOpts = (schools || []).map((s) => ({ id: s.id, name: s.name }));
    const yearOpts = (academicYears || [])
        .filter((y) => !form.school_id || y.school_id === Number(form.school_id))
        .map((y) => ({ id: y.id, name: y.name }));
    const termOpts = (academicTerms || [])
        .filter((t) => !form.academic_year_id || t.academic_year_id === Number(form.academic_year_id))
        .map((t) => ({ id: t.id, name: t.name }));
    const levelOpts = (levels || []).map((l) => ({ id: l.id, name: l.name }));
    const classOpts = (classItems || []).map((c) => ({ id: c.id, name: c.name }));
    const studentOpts = (studentsOptions || []).map((s) => ({ id: s.value, name: s.label }));
    const statusOpts = isArabic ? STATUS_OPTS_AR : STATUS_OPTS_EN;

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                {isArabic
                    ? 'سجل تسجيل طالب في حلقة. الطلاب المسجلين بالفعل في نفس الفصل الدراسي لن يظهروا في القائمة.'
                    : "Enroll a student in a class. Students already enrolled in the same term won't appear in the list."}
            </Alert>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المدرسة' : 'School'} type="select" name="school_id"
                            value={form.school_id}
                            onChange={(e) => setForm((p) => ({ ...p, school_id: e.target.value, academic_year_id: '', academic_term_id: '', student_id: '' }))}
                            options={schoolOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'السنة الدراسية' : 'Academic Year'} type="select" name="academic_year_id"
                            value={form.academic_year_id}
                            onChange={(e) => setForm((p) => ({ ...p, academic_year_id: e.target.value, academic_term_id: '', student_id: '' }))}
                            options={yearOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الفصل الدراسي' : 'Academic Term'} type="select" name="academic_term_id"
                            value={form.academic_term_id}
                            onChange={(e) => setForm((p) => ({ ...p, academic_term_id: e.target.value, student_id: '' }))}
                            options={termOpts} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الطالب' : 'Student'} type="select" name="student_id"
                            value={form.student_id}
                            onChange={(e) => setForm((p) => ({ ...p, student_id: e.target.value }))}
                            options={studentOpts} disabled={isEdit} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'المستوى' : 'Level'} type="select" name="level_id"
                            value={form.level_id}
                            onChange={(e) => setForm((p) => ({ ...p, level_id: e.target.value }))}
                            options={levelOpts} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormInput label={isArabic ? 'الحلقة' : 'Class'} type="select" name="class_id"
                            value={form.class_id}
                            onChange={(e) => setForm((p) => ({ ...p, class_id: e.target.value }))}
                            options={classOpts} />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'تاريخ الالتحاق' : 'Joined At'} type="date" name="joined_at"
                            value={form.joined_at} onChange={handleChange} required={false} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'تاريخ المغادرة' : 'Left At'} type="date" name="left_at"
                            value={form.left_at} onChange={handleChange} required={false} min={form.joined_at} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormInput label={isArabic ? 'الحالة' : 'Status'} type="select" name="status"
                            value={form.status}
                            onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                            options={statusOpts} />
                    </Grid>

                    <Grid item xs={12}>
                        <FormInput label={isArabic ? 'ملاحظات' : 'Notes'}
                            placeholder={isArabic ? 'ملاحظات إضافية...' : 'Additional notes...'}
                            type="textarea" name="notes" value={form.notes} onChange={handleChange} required={false} />
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit ? (isArabic ? 'جاري الحفظ...' : 'Saving...') : isEdit ? (isArabic ? 'تحديث' : 'Update') : (isArabic ? 'حفظ' : 'Save')}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditStudentEnrollment;
